import React, { Children, cloneElement, isValidElement, ReactElement, useState } from 'react';
import { Accordion, Sidebar } from '@hopskipdrive/react-components';

import { sidebarIcons } from '../sidebarIcons';
import { SidebarNavIcon } from '../SidebarNavIcon';
import { SidebarNavMenuLinkItem } from '../styles';

import { Divider, Header, Link, MobileAccordion, Popover, ProfileMenu, Subheader } from './SidebarProfileItem.styles';

export interface SidebarProfileItemProps {
  userName: string;
  accountName: string;
  isSettingsPage?: boolean;
  children: ReactElement;
}

export const SidebarProfileItem: React.FC<SidebarProfileItemProps & { isMobile?: boolean }> = ({
  userName,
  accountName,
  isMobile = false,
  isSettingsPage = false,
  children,
}) => {
  return isMobile ? (
    <SidebarProfileMobile userName={userName} accountName={accountName} isSettingsPage={isSettingsPage}>
      {children}
    </SidebarProfileMobile>
  ) : (
    <SidebarProfileDesktop userName={userName} accountName={accountName} isSettingsPage={isSettingsPage}>
      {children}
    </SidebarProfileDesktop>
  );
};

const SidebarProfileDesktop: React.FC<SidebarProfileItemProps> = ({
  userName,
  accountName,
  isSettingsPage,
  children,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  return (
    <>
      <SidebarNavMenuLinkItem
        component="button"
        type="button"
        data-testid="profile-link-item"
        active={isSettingsPage}
        aria-expanded={open}
        aria-controls={open ? 'sidebar-profile-menu' : undefined}
        onClick={handleClick}
        $truncateLabel
      >
        <SidebarNavIcon icon={isSettingsPage ? sidebarIcons.profile.filled : sidebarIcons.profile.outlined} />
        <Sidebar.MenuLinkText>{userName.split(' ')[0]}</Sidebar.MenuLinkText>
      </SidebarNavMenuLinkItem>
      <Popover
        id="sidebar-profile-menu"
        data-testid="profile-menu"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
      >
        <ProfileMenu>
          <Header variant="h2" textColor="white">
            {userName}
          </Header>
          <Subheader variant="body1">{accountName}</Subheader>
          <Divider />
          {Children.map(children, (child) => {
            if (isValidElement(child)) {
              return <Link onClick={handleClose}>{cloneElement(child)}</Link>;
            }

            return null;
          })}
        </ProfileMenu>
      </Popover>
    </>
  );
};

const SidebarProfileMobile: React.FC<SidebarProfileItemProps> = ({
  userName,
  accountName,
  isSettingsPage,
  children,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <MobileAccordion style={{ backgroundColor: 'transparent' }} onChange={(_, expanded) => setIsExpanded(expanded)}>
      <Accordion.Header expandIcon={''}>
        <Accordion.Title>
          <SidebarNavMenuLinkItem component="span" data-testid="profile-link-item" active={isSettingsPage} $mobile>
            <SidebarNavIcon icon={isSettingsPage ? sidebarIcons.profile.filled : sidebarIcons.profile.outlined} />
            <Sidebar.MenuLinkText>
              {!isExpanded ? (
                userName.split(' ')[0]
              ) : (
                <>
                  <Header variant="h2">{userName}</Header>
                  <Subheader variant="body1" $selected={isSettingsPage}>
                    {accountName}
                  </Subheader>
                </>
              )}
            </Sidebar.MenuLinkText>
          </SidebarNavMenuLinkItem>
        </Accordion.Title>
      </Accordion.Header>
      <Accordion.Body>
        {Children.map(children, (child) => {
          if (isValidElement(child)) {
            return <Link>{cloneElement(child)}</Link>;
          }

          return null;
        })}
      </Accordion.Body>
    </MobileAccordion>
  );
};
